import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Route, Star, Signpost, Plus, Trash2, Send } from "lucide-react";

export default function PilgrimCare() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [contacts, setContacts] = useState([]);
  const [stages, setStages] = useState([]);
  const [sel, setSel] = useState({ contact_id: "", stage: "", notify: true });
  const [itinerary, setItinerary] = useState([]);
  const [itForm, setItForm] = useState({ day: "", title: "", detail: "" });
  const [reviews, setReviews] = useState([]);
  const [rvForm, setRvForm] = useState({ name: "", location: "", text: "", rating: 5 });

  const load = useCallback(async () => {
    const [c, st, it, rv] = await Promise.all([
      api.get("/contacts").catch(() => ({ data: [] })),
      api.get("/journey/stages").catch(() => ({ data: [] })),
      api.get("/itinerary").catch(() => ({ data: [] })),
      api.get("/testimonials").catch(() => ({ data: [] })),
    ]);
    setContacts(Array.isArray(c.data) ? c.data : (c.data.contacts || []));
    setStages(st.data); setItinerary(it.data); setReviews(rv.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const setJourney = async () => {
    if (!sel.contact_id || !sel.stage) { toast.error("Pick a pilgrim and a stage"); return; }
    try {
      const { data } = await api.put(`/contacts/${sel.contact_id}/journey`, { stage: sel.stage, notify: sel.notify });
      toast.success(`Stage set to "${sel.stage}"${data.notified ? " · WhatsApp sent" : ""}`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const addIt = async () => {
    if (!itForm.day || !itForm.title) { toast.error("Day and title required"); return; }
    try { await api.post("/itinerary", { ...itForm, order: itinerary.length }); setItForm({ day: "", title: "", detail: "" }); toast.success("Added"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const delIt = async (id) => { await api.delete(`/itinerary/${id}`); load(); };

  const addRv = async () => {
    if (!rvForm.name || !rvForm.text) { toast.error("Name and review required"); return; }
    try { await api.post("/testimonials", rvForm); setRvForm({ name: "", location: "", text: "", rating: 5 }); toast.success("Review added"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const delRv = async (id) => { await api.delete(`/testimonials/${id}`); load(); };

  return (
    <div>
      <PageHeader eyebrow="Pilgrim Care" title="Journey, Itinerary & Reviews" subtitle="Update each pilgrim's status, publish the day-by-day itinerary, and manage website testimonials" />
      <div className="p-4 sm:p-8">
        <Tabs defaultValue="journey">
          <TabsList className="bg-card/50 border border-gold/25">
            <TabsTrigger value="journey" data-testid="care-tab-journey" className="gap-1.5"><Route className="w-4 h-4" /> Journey</TabsTrigger>
            <TabsTrigger value="itinerary" data-testid="care-tab-itinerary" className="gap-1.5"><Signpost className="w-4 h-4" /> Itinerary</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="care-tab-reviews" className="gap-1.5"><Star className="w-4 h-4" /> Reviews</TabsTrigger>
          </TabsList>

          <TabsContent value="journey" className="mt-5">
            <Card className="p-5 bg-card/40 border-gold/30 max-w-xl" data-testid="journey-setter">
              <p className="font-serif font-semibold text-white mb-3">Update Pilgrim Journey Status</p>
              <div className="space-y-3">
                <div><Label className="text-xs text-muted-foreground">Pilgrim</Label>
                  <Select value={sel.contact_id} onValueChange={(v) => setSel({ ...sel, contact_id: v })}>
                    <SelectTrigger data-testid="journey-contact-select" className="mt-1 bg-card/60"><SelectValue placeholder="Select pilgrim" /></SelectTrigger>
                    <SelectContent>{contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.phone}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label className="text-xs text-muted-foreground">Stage</Label>
                  <Select value={sel.stage} onValueChange={(v) => setSel({ ...sel, stage: v })}>
                    <SelectTrigger data-testid="journey-stage-select" className="mt-1 bg-card/60"><SelectValue placeholder="Select stage" /></SelectTrigger>
                    <SelectContent>{stages.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select></div>
                <label className="flex items-center gap-2 text-xs text-muted-foreground"><input type="checkbox" checked={sel.notify} onChange={(e) => setSel({ ...sel, notify: e.target.checked })} data-testid="journey-notify-check" /> Notify pilgrim on WhatsApp</label>
                <Button data-testid="journey-set-btn" onClick={setJourney} className="bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold gap-2"><Send className="w-4 h-4" /> Update & Notify</Button>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="itinerary" className="mt-5 space-y-4">
            {isOwner && (
              <Card className="p-5 bg-card/40 border-border/60 max-w-xl" data-testid="itinerary-form">
                <div className="grid grid-cols-4 gap-2">
                  <div><Label className="text-xs text-muted-foreground">Day</Label><Input data-testid="it-day" value={itForm.day} onChange={(e) => setItForm({ ...itForm, day: e.target.value })} className="mt-1 bg-card/60" placeholder="Day 1" /></div>
                  <div className="col-span-3"><Label className="text-xs text-muted-foreground">Title</Label><Input data-testid="it-title" value={itForm.title} onChange={(e) => setItForm({ ...itForm, title: e.target.value })} className="mt-1 bg-card/60" placeholder="Arrive Jeddah → Makkah hotel" /></div>
                </div>
                <div className="mt-2"><Label className="text-xs text-muted-foreground">Detail</Label><Textarea data-testid="it-detail" value={itForm.detail} onChange={(e) => setItForm({ ...itForm, detail: e.target.value })} className="mt-1 bg-card/60 min-h-[60px]" /></div>
                <Button data-testid="it-add-btn" onClick={addIt} className="mt-3 bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold gap-2"><Plus className="w-4 h-4" /> Add Day</Button>
              </Card>
            )}
            <div className="space-y-2 max-w-2xl">
              {itinerary.map((it) => (
                <Card key={it.id} data-testid={`it-row-${it.id}`} className="p-3 bg-card/40 border-border/60 flex gap-3 items-start">
                  <Badge variant="outline" className="border-gold/40 text-gold">{it.day}</Badge>
                  <div className="min-w-0 flex-1"><p className="text-sm text-white font-medium">{it.title}</p>{it.detail && <p className="text-xs text-muted-foreground mt-0.5">{it.detail}</p>}</div>
                  {isOwner && <Button size="icon" variant="ghost" data-testid={`it-del-${it.id}`} onClick={() => delIt(it.id)} className="h-7 w-7 text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>}
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="reviews" className="mt-5 space-y-4">
            {isOwner && (
              <Card className="p-5 bg-card/40 border-border/60 max-w-xl" data-testid="review-form">
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs text-muted-foreground">Name</Label><Input data-testid="rv-name" value={rvForm.name} onChange={(e) => setRvForm({ ...rvForm, name: e.target.value })} className="mt-1 bg-card/60" /></div>
                  <div><Label className="text-xs text-muted-foreground">Location</Label><Input data-testid="rv-location" value={rvForm.location} onChange={(e) => setRvForm({ ...rvForm, location: e.target.value })} className="mt-1 bg-card/60" placeholder="Kolkata" /></div>
                </div>
                <div className="mt-2"><Label className="text-xs text-muted-foreground">Review</Label><Textarea data-testid="rv-text" value={rvForm.text} onChange={(e) => setRvForm({ ...rvForm, text: e.target.value })} className="mt-1 bg-card/60 min-h-[60px]" /></div>
                <div className="mt-2"><Label className="text-xs text-muted-foreground">Rating</Label>
                  <Select value={String(rvForm.rating)} onValueChange={(v) => setRvForm({ ...rvForm, rating: +v })}>
                    <SelectTrigger data-testid="rv-rating" className="mt-1 bg-card/60 w-28"><SelectValue /></SelectTrigger>
                    <SelectContent>{[5, 4, 3, 2, 1].map((n) => <SelectItem key={n} value={String(n)}>{n} ★</SelectItem>)}</SelectContent>
                  </Select></div>
                <Button data-testid="rv-add-btn" onClick={addRv} className="mt-3 bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold gap-2"><Plus className="w-4 h-4" /> Add Review</Button>
              </Card>
            )}
            <div className="grid sm:grid-cols-2 gap-3 max-w-3xl">
              {reviews.map((r) => (
                <Card key={r.id} data-testid={`rv-row-${r.id}`} className="p-4 bg-card/40 border-border/60">
                  <div className="flex items-center justify-between">
                    <p className="font-serif text-white">{r.name}{r.location ? <span className="text-xs text-muted-foreground"> · {r.location}</span> : ""}</p>
                    {isOwner && <Button size="icon" variant="ghost" data-testid={`rv-del-${r.id}`} onClick={() => delRv(r.id)} className="h-7 w-7 text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>}
                  </div>
                  <p className="text-gold text-xs mt-0.5">{"★".repeat(r.rating)}</p>
                  <p className="text-xs text-muted-foreground mt-2 leading-relaxed">"{r.text}"</p>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
